import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { type ChartBar, rangeLabel } from './promoDashboard.js';

/**
 * 375: what was given per day (or week), stacked by kind. Loaded lazily, so the figures never wait for the chart's
 * code. Colours are the admin's tokens, read as CSS variables by the SVG.
 */
export const KIND_COLOUR = {
  credit: 'var(--color-accent-orange)',
  voucher: 'var(--color-primary)',
  personal: 'var(--color-semantic-success-text)',
} as const;
export const KIND_LABEL = {
  credit: 'Credit used',
  voucher: 'Voucher discounts',
  personal: 'Personal discounts',
} as const;

const tick = (v: number) =>
  v >= 1000 ? `${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k` : String(Math.round(v));
const money = (v: number) => `RM ${v.toLocaleString('en-MY', { maximumFractionDigits: 2 })}`;

interface TooltipProps {
  active?: boolean;
  payload?: Array<{ payload?: unknown }>;
}

function PromoTooltip({ active, payload }: TooltipProps) {
  const bar = payload?.[0]?.payload as ChartBar | undefined;
  if (!active || !bar) return null;
  return (
    <div className="rounded-md border border-hairline bg-canvas px-3 py-2 text-xs text-ink shadow-sm">
      <p className="mb-1 font-semibold">
        {bar.from === bar.to
          ? rangeLabel(bar.from, bar.to).split(' – ')[1]
          : rangeLabel(bar.from, bar.to)}
      </p>
      {(['credit', 'voucher', 'personal'] as const).map((k) => (
        <p key={k} className="flex justify-between gap-4">
          <span>
            <span
              className="mr-1.5 inline-block h-2 w-2 rounded-full"
              style={{ background: KIND_COLOUR[k] }}
            />
            {KIND_LABEL[k]}
          </span>
          <span>{money(bar[k])}</span>
        </p>
      ))}
      <p className="mt-1 flex justify-between gap-4 border-t border-hairline pt-1 font-semibold">
        <span>Total</span>
        <span>{money(bar.credit + bar.voucher + bar.personal)}</span>
      </p>
    </div>
  );
}

export default function PromoChart({ bars }: { bars: ChartBar[] }) {
  return (
    <ResponsiveContainer width="100%" height={200}>
      <BarChart data={bars} margin={{ top: 8, right: 4, bottom: 0, left: 0 }} barCategoryGap={2}>
        <CartesianGrid vertical={false} stroke="var(--color-hairline)" strokeDasharray="3 3" />
        <XAxis
          dataKey="label"
          tickLine={false}
          axisLine={{ stroke: 'var(--color-hairline-strong)' }}
          tick={{ fill: 'var(--color-steel)', fontSize: 11 }}
          interval="preserveStartEnd"
          minTickGap={12}
        />
        <YAxis
          width={36}
          tickLine={false}
          axisLine={false}
          tick={{ fill: 'var(--color-steel)', fontSize: 11 }}
          tickFormatter={tick}
        />
        <Tooltip
          cursor={{ fill: 'var(--color-surface-feature)' }}
          content={<PromoTooltip />}
          isAnimationActive={false}
        />
        {(['credit', 'voucher', 'personal'] as const).map((k) => (
          <Bar
            key={k}
            dataKey={k}
            stackId="given"
            fill={KIND_COLOUR[k]}
            isAnimationActive={false}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}
